// Type-system utilities — the house standard for "make illegal states unrepresentable"
// and exhaustive handling. Used across both apps via `@recruiter/shared/types`.

/**
 * Exhaustiveness guard for discriminated unions. Put it in the `default` branch of a
 * `switch` over a union's discriminant: if a new variant is added and not handled, the
 * call becomes a COMPILE error (the argument is no longer `never`). At runtime it throws,
 * surfacing the unhandled case loudly instead of silently falling through.
 *
 * @example
 * switch (event.type) {
 *   case "block-add": return addBlock(event);
 *   case "block-delete": return deleteBlock(event);
 *   default: return assertNever(event); // adding a new event type fails typecheck here
 * }
 */
export function assertNever(value: never, message = "Unhandled union variant"): never {
  throw new Error(`${message}: ${JSON.stringify(value)}`);
}

/**
 * Compile-time exhaustiveness for an OPEN wire union: an unhandled variant fails typecheck
 * here (the argument is no longer `never`), but — UNLIKE `assertNever` — this does NOT throw.
 * Reaching it at runtime is an expected forward-compat case (a newer backend ships an event
 * this build doesn't model yet); the caller logs + degrades gracefully. Reserve `assertNever`
 * (throws) for CLOSED unions / internal UI state where an unhandled case is a genuine bug.
 *
 * @example
 * if (event.type === "block-add") return addBlock(event);
 * if (event.type === "block-delete") return deleteBlock(event);
 * log.warn("unknown event", { type: (event as { type?: string }).type });
 * assertExhaustive(event); // adding a modeled variant without a branch fails typecheck here
 */
export function assertExhaustive(_value: never): void {}

/**
 * Nominal/branded type helper. `Brand<string, "BuyerId">` is assignable FROM nothing but a
 * value explicitly tagged as a BuyerId, so a `ProjectId` can't be passed where a `BuyerId`
 * is expected even though both are strings at runtime. Construct with the matching `asXId`
 * helper at the boundary where the value enters the app (API response, route param).
 */
export type Brand<T, B extends string> = T & { readonly __brand: B };

export type BuyerId = Brand<string, "BuyerId">;
export type ProjectId = Brand<string, "ProjectId">;
export type TurnId = Brand<string, "TurnId">;
export type SectionId = Brand<string, "SectionId">;
export type BlockId = Brand<string, "BlockId">;

export const asBuyerId = (s: string): BuyerId => s as BuyerId;
export const asProjectId = (s: string): ProjectId => s as ProjectId;
export const asTurnId = (s: string): TurnId => s as TurnId;
export const asSectionId = (s: string): SectionId => s as SectionId;
export const asBlockId = (s: string): BlockId => s as BlockId;
